import { useEffect, useMemo, useState } from 'react';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import RefreshIcon from '@mui/icons-material/Refresh';
import { hrApi } from '@/api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { DateRangeFields } from '@/components/ui/DateRangeFields';
import { Input } from '@/components/ui/Input';
import { RecordDataTable } from '@/components/ui/RecordDataTable';
import { Skeleton } from '@/components/ui/Skeleton';
import { StatCard } from '@/components/ui/StatCard';
import { Textarea } from '@/components/ui/Textarea';
import { LeaveTypeSelect } from '@/components/selects';
import { HrEmployeeProfileHeader } from '@/modules/hr/components/HrEmployeeProfileHeader';
import { HrSectionTabPanel, HrSectionTabs } from '@/modules/hr/components/HrSectionTabs';
import { hrFormatLeaveDays, hrFormatMinutes } from '@/modules/hr/components/hrCalendarUtils';
import { hrStatusBadgeVariant } from '@/modules/hr/components/hrStatusUtils';
import { KpiGrid } from '@/components/ui/KpiGrid';
import type { Employee360Response } from '@/types/hr';
import { showApiError } from '@/utils/apiError';
import { downloadBlob } from '@/utils/downloadBlob';
import { notify } from '@/core/toast/notify';

const ESS_TABS = [
  { id: 'home', label: 'Home' },
  { id: 'leave', label: 'Leave' },
  { id: 'time', label: 'Time' },
  { id: 'pay', label: 'Pay' },
  { id: 'help', label: 'Help' },
] as const;

type EssTabId = (typeof ESS_TABS)[number]['id'];

function EssLoadingSkeleton() {
  return (
    <div className="space-y-4" aria-busy="true" aria-label="Loading self-service portal">
      <Skeleton className="h-28 w-full rounded-xl" />
      <div className="grid gap-3 sm:grid-cols-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-20 rounded-xl" />
        ))}
      </div>
      <Skeleton className="h-10 w-full" />
      <Skeleton className="h-64 w-full rounded-xl" />
    </div>
  );
}

export function HrEssPortalPage() {
  const [profile, setProfile] = useState<Employee360Response | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<EssTabId>('home');
  const [checkingIn, setCheckingIn] = useState(false);
  const [leaveTypeId, setLeaveTypeId] = useState('');
  const [leaveStart, setLeaveStart] = useState('');
  const [leaveEnd, setLeaveEnd] = useState('');
  const [leaveReason, setLeaveReason] = useState('');
  const [balances, setBalances] = useState<unknown[]>([]);
  const [applyingLeave, setApplyingLeave] = useState(false);
  const [ticketSubject, setTicketSubject] = useState('');
  const [ticketDesc, setTicketDesc] = useState('');
  const [submittingTicket, setSubmittingTicket] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const p = await hrApi.essProfile();
      setProfile(p);
      if (p?.employee.id) {
        try {
          setBalances(await hrApi.leaveBalances(p.employee.id));
        } catch {
          setBalances([]);
        }
      } else {
        setBalances([]);
      }
    } catch (err) {
      showApiError(err, 'Failed to load ESS profile');
      setProfile(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void refresh();
  }, []);

  const geoCheckIn = async () => {
    if (!profile?.employee.id) return;
    setCheckingIn(true);
    try {
      const pos = await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, { enableHighAccuracy: true, timeout: 15000 });
      });
      await hrApi.markAttendance({
        employeeId: profile.employee.id,
        checkInMode: 'GEO',
        checkInLatitude: pos.coords.latitude,
        checkInLongitude: pos.coords.longitude,
      });
      notify.success('Geo check-in recorded');
      await refresh();
    } catch (err) {
      showApiError(err, 'Geo check-in failed — ensure location permission and geo-fence is configured');
    } finally {
      setCheckingIn(false);
    }
  };

  const applyLeave = async () => {
    if (!leaveTypeId || !leaveStart || !leaveEnd) {
      notify.error('Leave type, start, and end dates are required');
      return;
    }
    setApplyingLeave(true);
    try {
      await hrApi.essApplyLeave({
        leaveTypeId: Number(leaveTypeId),
        startDate: leaveStart,
        endDate: leaveEnd,
        reason: leaveReason || undefined,
      });
      notify.success('Leave request submitted');
      setLeaveReason('');
      await refresh();
      setActiveTab('leave');
    } catch (err) {
      showApiError(err, 'Leave application failed');
    } finally {
      setApplyingLeave(false);
    }
  };

  const raiseTicket = async () => {
    if (!ticketSubject.trim()) {
      notify.error('Subject is required');
      return;
    }
    setSubmittingTicket(true);
    try {
      await hrApi.essRaiseTicket(ticketSubject, ticketDesc);
      notify.success('HR ticket submitted');
      setTicketSubject('');
      setTicketDesc('');
    } catch (err) {
      showApiError(err, 'Helpdesk ticket failed');
    } finally {
      setSubmittingTicket(false);
    }
  };

  const leaveColumns: Column<NonNullable<Employee360Response['recentLeaves']>[number]>[] = useMemo(
    () => [
      { key: 'leaveType', header: 'Type' },
      {
        key: 'status',
        header: 'Status',
        render: (r) => <Badge variant={hrStatusBadgeVariant(r.status)}>{r.status}</Badge>,
      },
      { key: 'totalDays', header: 'Days', render: (r) => hrFormatLeaveDays(r.totalDays) },
    ],
    [],
  );

  const attendanceColumns: Column<NonNullable<Employee360Response['recentAttendance']>[number]>[] = useMemo(
    () => [
      { key: 'date', header: 'Date', render: (r) => r.date ?? '—' },
      {
        key: 'status',
        header: 'Status',
        render: (r) => <Badge variant={hrStatusBadgeVariant(r.status)}>{r.status}</Badge>,
      },
      {
        key: 'worked',
        header: 'Worked',
        render: (r) => (r.workedMinutes != null ? hrFormatMinutes(r.workedMinutes) : '—'),
      },
    ],
    [],
  );

  const payrollColumns: Column<NonNullable<Employee360Response['recentPayroll']>[number]>[] = useMemo(
    () => [
      { key: 'runCode', header: 'Pay run' },
      { key: 'netAmount', header: 'Net amount' },
      {
        key: 'status',
        header: 'Status',
        render: (r) => <Badge variant={hrStatusBadgeVariant(r.status)}>{r.status}</Badge>,
      },
      {
        key: 'actions',
        header: 'Download',
        actions: true,
        render: (r) => (
          <Button
            variant="secondary"
            size="sm"
            aria-label={`Download payslip for run ${r.runCode}`}
            onClick={async () => {
              try {
                const blob = await hrApi.downloadEssPayslipPdf(r.runId);
                downloadBlob(blob, `payslip-${r.runCode}.pdf`);
              } catch (err) {
                showApiError(err, 'Payslip download failed');
              }
            }}
          >
            PDF
          </Button>
        ),
      },
    ],
    [],
  );

  const pendingLeaveCount = useMemo(
    () => (profile?.recentLeaves ?? []).filter((l) => l.status.toUpperCase().includes('PENDING')).length,
    [profile?.recentLeaves],
  );

  const tabsWithBadges = ESS_TABS.map((t) =>
    t.id === 'leave' && pendingLeaveCount > 0 ? { ...t, badge: pendingLeaveCount } : t,
  );

  const profileSubtitle = profile?.employment
    ? [profile.employment.designationName, profile.employment.departmentName].filter(Boolean).join(' · ')
    : profile?.employee.designation;

  return (
    <div className="page-content">
      <PageHeader
        title="Employee self-service"
        description="Your profile, time, leave, pay, and HR support in one place"
        breadcrumbs={[{ label: 'Org', to: '/org' }, { label: 'HR', to: '/hr' }, { label: 'Self-service' }]}
        actions={
          <Button variant="secondary" onClick={() => void refresh()} disabled={loading} aria-label="Refresh portal data">
            <RefreshIcon fontSize="small" className="mr-1.5" aria-hidden />
            Refresh
          </Button>
        }
      />

      {loading && !profile && <EssLoadingSkeleton />}

      {profile && (
        <div className="flex flex-col gap-4">
          <HrEmployeeProfileHeader
            firstName={profile.employee.firstName}
            lastName={profile.employee.lastName}
            employeeCode={profile.employee.employeeCode}
            email={profile.employee.email}
            subtitle={profileSubtitle || undefined}
            compact
            actions={
              <Button loading={checkingIn} onClick={() => void geoCheckIn()} aria-label="Record geo check-in">
                <LocationOnIcon fontSize="small" className="mr-1.5" aria-hidden />
                {checkingIn ? 'Checking in…' : 'Geo check-in'}
              </Button>
            }
          />

          <KpiGrid columns={3}>
            <StatCard title="Leave balances" value={String(balances.length)} />
            <StatCard
              title="Recent attendance"
              value={String((profile.recentAttendance ?? []).length)}
            />
            <StatCard title="Payslips available" value={String((profile.recentPayroll ?? []).length)} />
          </KpiGrid>

          <div className="card flex min-h-[480px] flex-col overflow-hidden p-4">
            <HrSectionTabs
              idPrefix="ess"
              tabs={tabsWithBadges}
              active={activeTab}
              onChange={(id) => setActiveTab(id as EssTabId)}
            />

            <div className="min-h-0 flex-1 overflow-y-auto pt-4">
              <HrSectionTabPanel idPrefix="ess" tabId="home" active={activeTab === 'home'} className="space-y-4">
                <div className="grid gap-4 lg:grid-cols-2">
                  <section className="rounded-lg border border-slate-100 p-4 dark:border-slate-800">
                    <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Quick actions</h3>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <Button size="sm" variant="secondary" onClick={() => setActiveTab('leave')}>
                        Apply for leave
                      </Button>
                      <Button size="sm" variant="secondary" onClick={() => setActiveTab('time')}>
                        View attendance
                      </Button>
                      <Button size="sm" variant="secondary" onClick={() => setActiveTab('pay')}>
                        View payslips
                      </Button>
                      <Button size="sm" variant="secondary" onClick={() => setActiveTab('help')}>
                        Contact HR
                      </Button>
                    </div>
                  </section>

                  <section className="rounded-lg border border-slate-100 p-4 dark:border-slate-800">
                    <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Leave balances</h3>
                    <div className="mt-3">
                      <RecordDataTable
                        data={balances}
                        embedded
                        pageSize={5}
                        emptyTitle="No leave balances"
                        emptyDescription="Balances will appear when leave policies are assigned."
                      />
                    </div>
                  </section>
                </div>

                <section className="rounded-lg border border-slate-100 p-4 dark:border-slate-800">
                  <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Recent activity</h3>
                  <div className="mt-3 grid gap-4 lg:grid-cols-2">
                    <DataTable
                      columns={leaveColumns}
                      data={(profile.recentLeaves ?? []).slice(0, 5)}
                      keyField="id"
                      embedded
                      pageSize={5}
                      emptyTitle="No recent leave"
                    />
                    <DataTable
                      columns={attendanceColumns}
                      data={(profile.recentAttendance ?? []).slice(0, 5)}
                      keyField="id"
                      embedded
                      pageSize={5}
                      emptyTitle="No recent attendance"
                    />
                  </div>
                </section>
              </HrSectionTabPanel>

              <HrSectionTabPanel idPrefix="ess" tabId="leave" active={activeTab === 'leave'} className="space-y-4">
                <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)]">
                  <section
                    className="rounded-lg border border-slate-100 p-4 dark:border-slate-800"
                    aria-labelledby="ess-apply-leave-heading"
                  >
                    <h3 id="ess-apply-leave-heading" className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                      Apply for leave
                    </h3>
                    <div className="mt-3 space-y-3">
                      <LeaveTypeSelect value={leaveTypeId} onChange={setLeaveTypeId} required />
                      <DateRangeFields
                        from={leaveStart}
                        to={leaveEnd}
                        onFromChange={setLeaveStart}
                        onToChange={setLeaveEnd}
                        fromLabel="Start date"
                        toLabel="End date"
                      />
                      <Textarea
                        label="Reason (optional)"
                        value={leaveReason}
                        onChange={(e) => setLeaveReason(e.target.value)}
                        rows={3}
                      />
                      <Button loading={applyingLeave} onClick={() => void applyLeave()}>
                        Submit leave request
                      </Button>
                    </div>
                  </section>

                  <section className="rounded-lg border border-slate-100 p-4 dark:border-slate-800">
                    <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Your leave balances</h3>
                    <div className="mt-3">
                      <RecordDataTable data={balances} embedded pageSize={6} emptyTitle="No leave balances" />
                    </div>
                  </section>
                </div>

                <section className="rounded-lg border border-slate-100 p-4 dark:border-slate-800">
                  <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Leave history</h3>
                  <div className="mt-3">
                    <DataTable
                      columns={leaveColumns}
                      data={profile.recentLeaves ?? []}
                      keyField="id"
                      embedded
                      pageSize={8}
                      emptyTitle="No leave history"
                      emptyDescription="Submitted leave requests will appear here."
                    />
                  </div>
                </section>
              </HrSectionTabPanel>

              <HrSectionTabPanel idPrefix="ess" tabId="time" active={activeTab === 'time'} className="space-y-4">
                <section className="rounded-lg border border-slate-100 p-4 dark:border-slate-800">
                  <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                    <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Attendance history</h3>
                    <Button loading={checkingIn} variant="secondary" size="sm" onClick={() => void geoCheckIn()}>
                      <LocationOnIcon fontSize="small" className="mr-1" aria-hidden />
                      Geo check-in
                    </Button>
                  </div>
                  <DataTable
                    columns={attendanceColumns}
                    data={profile.recentAttendance ?? []}
                    keyField="id"
                    embedded
                    pageSize={10}
                    emptyTitle="No attendance records"
                    emptyDescription="Your check-ins and attendance will appear here."
                  />
                </section>
              </HrSectionTabPanel>

              <HrSectionTabPanel idPrefix="ess" tabId="pay" active={activeTab === 'pay'} className="space-y-4">
                <section className="rounded-lg border border-slate-100 p-4 dark:border-slate-800">
                  <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Payslips</h3>
                  <p className="mt-1 text-xs text-slate-500">Download PDF payslips for processed payroll runs.</p>
                  <div className="mt-3">
                    <DataTable
                      columns={payrollColumns}
                      data={profile.recentPayroll ?? []}
                      keyField="runId"
                      embedded
                      pageSize={8}
                      emptyTitle="No payslips available"
                      emptyDescription="Payslips appear after payroll is processed."
                    />
                  </div>
                </section>
              </HrSectionTabPanel>

              <HrSectionTabPanel idPrefix="ess" tabId="help" active={activeTab === 'help'} className="space-y-4">
                <section
                  className="mx-auto max-w-xl rounded-lg border border-slate-100 p-4 dark:border-slate-800"
                  aria-labelledby="ess-helpdesk-heading"
                >
                  <h3 id="ess-helpdesk-heading" className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                    HR helpdesk
                  </h3>
                  <p className="mt-1 text-xs text-slate-500">
                    Submit a request to HR. You will receive updates through your usual HR channels.
                  </p>
                  <div className="mt-4 space-y-3">
                    <Input
                      label="Subject"
                      value={ticketSubject}
                      onChange={(e) => setTicketSubject(e.target.value)}
                      required
                      autoComplete="off"
                    />
                    <Textarea
                      label="Description"
                      value={ticketDesc}
                      onChange={(e) => setTicketDesc(e.target.value)}
                      rows={4}
                      placeholder="Describe your question or issue…"
                    />
                    <Button loading={submittingTicket} onClick={() => void raiseTicket()}>
                      Submit ticket
                    </Button>
                  </div>
                </section>
              </HrSectionTabPanel>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
